import {IUser} from 'types/user'; // Type for user object
import React, {createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState} from 'react';

/**
 * WHAT IS THIS: This interface defines what data and functions our UserContext will provide to all components
 * WHY WE USE IT: TypeScript needs to know exactly what properties our context will have
 * EXAMPLE: When a component uses useUserContext(), it gets these 4 things: user, setUser, userLoading, setUserLoading
 */
interface IUserContext {
  user: IUser | null; // The currently logged-in user's data (null if not logged in)
  setUser: (userData: IUser | null) => void; // Function to update user data AND save it to localStorage automatically
  userLoading: boolean; // True when we're fetching user data, false when done
  setUserLoading: Dispatch<SetStateAction<boolean>>; // Function to update the loading state (React's built-in setState type)
}

/**
 * WHAT IS THIS: createContext creates a "data tunnel" that can pass data to any component in the app
 * WHY WE USE IT: Instead of passing user data through props to every single component, we create a "tunnel"
 * EXAMPLE: Without context: App → Header → Info (passing user through 3 components)
 *          With context: App sets user in context, Info reads directly from context (skips Header)
 * 
 * The object below provides default values if no Provider is found (shouldn't happen in normal use)
 */
const UserContext = createContext<IUserContext>({
  user: null,
  setUser: () => {},
  userLoading: false,
  setUserLoading: () => {}
});

/**
 * WHAT IS THIS: This is the "Provider" component that manages user data for the entire app
 * WHY WE USE IT: This component wraps around the entire app and provides user data to any component that needs it
 * HOW IT WORKS: 
 *   1. Stores user data in React state (in memory)
 *   2. Automatically saves user data to localStorage (survives page refresh)
 *   3. Automatically restores user data from localStorage when app starts
 *   4. Provides both the data and functions to update it to all child components
 */
export const UserContextProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  /**
   * WHAT IS THIS: useState creates a piece of state (data that can change over time)
   * WHY WE USE IT: We need to store the current user's data and track if we're loading user data
   * EXAMPLE: user starts as null, becomes {email: "john@example.com", ...} after login, becomes null after logout
   */
  const [user, setUser] = useState<IUser | null>(null); // Current user data (null = not logged in)
  const [userLoading, setUserLoading] = useState<boolean>(false); // Are we currently fetching user data?

  /**
   * WHAT IS THIS: useCallback creates a function that doesn't get recreated on every render
   * WHY WE USE IT: Without useCallback, a new function is created every time this component re-renders,
   *                which would cause ALL components using this context to re-render unnecessarily
   * HOW IT WORKS: React remembers this function and reuses it until the dependencies change ([] = never changes)
   * 
   * THIS FUNCTION DOES TWO THINGS:
   * 1. Updates the user state in React (so UI updates immediately)
   * 2. Saves the user data to localStorage (so it survives page refresh)
   * 
   * EXAMPLE: setUserWithPersistence({email: "john@example.com"})
   *          → Updates React state AND saves to localStorage
   */
  const setUserWithPersistence = useCallback((userData: IUser | null) => {
    // Step 1: Update React state (this triggers UI updates)
    setUser(userData);
    
    // Step 2: Handle localStorage persistence
    if (userData) {
      // User is logging in - save their data to localStorage
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      // User is logging out - remove their data from localStorage
      localStorage.removeItem('user');
    }
  }, []); // Empty dependency array [] means this function never changes

  /**
   * WHAT IS THIS: useEffect runs code when the component first loads (and optionally when things change)
   * WHY WE USE IT: We want to check if there's saved user data in localStorage when the app starts
   * HOW IT WORKS: [] dependency array means this only runs ONCE when the component first mounts
   * 
   * SCENARIO: User was logged in, closed browser, opened browser again
   * RESULT: This code finds their saved data and logs them back in automatically
   * 
   * EXAMPLE: localStorage has '{"email":"john@example.com","username":"john123"}'
   *          → This code parses it and sets user state → User appears logged in immediately
   */
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      // Found saved user data - parse it from JSON string and restore user state
      setUser(JSON.parse(savedUser));
    }
    // If no saved user data found, user stays null (logged out state)
  }, []); // [] means run only once when component first loads

  /**
   * WHAT IS THIS: The Provider component that makes user data available to all child components
   * WHY WE USE IT: This creates the "data tunnel" - any component inside {children} can access user data
   * HOW IT WORKS: We pass our state and functions through the "value" prop
   * 
   * KEY POINT: We pass setUserWithPersistence as "setUser" 
   * RESULT: When other components call setUser(), they automatically get localStorage persistence
   * 
   * EXAMPLE: Header component calls setUser(null) to log out
   *          → Actually calls setUserWithPersistence(null)
   *          → Updates React state AND clears localStorage
   *          → Header doesn't need to know about localStorage at all!
   */
  return (
    <UserContext.Provider value={{
      user: user, 
      setUser: setUserWithPersistence, // Other components get our enhanced setUser function
      userLoading: userLoading, 
      setUserLoading: setUserLoading
    }}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * WHAT IS THIS: A custom hook that provides easy access to user data from any component
 * WHY WE USE IT: Instead of importing useContext and UserContext in every component, we create this shortcut
 * HOW TO USE IT: const {user, setUser, userLoading} = useUserContext();
 * 
 * SAFETY CHECK: If someone tries to use this hook outside the UserContextProvider, we throw an error
 * EXAMPLE: If you use useUserContext() in a component that's not wrapped by UserContextProvider, you get a helpful error message
 */
export const useUserContext = () => {
  const context = useContext(UserContext);

  // Safety check: make sure this hook is used inside UserContextProvider
  if (!context) {
    throw new Error('useUserContext must be used within UserContextProvider');
  }

  return context;
};
